import { roundMoney } from './soldBy';

/**
 * CASH bill round-off: nearest ₹1, half-up (0.50 → 1).
 * Lines stay at paise; only the bill total is rounded.
 * UPI / CARD: no round-off — collect exact paise.
 *
 * @param {number} merchandiseTotal  subtotal − discount (unrounded payable before cash round)
 * @returns {{ merchandise: number, payable: number, round_off: number }}
 */
export function cashRoundOff(merchandiseTotal) {
  const merchandise = roundMoney(merchandiseTotal);
  const payable = Math.round(merchandise);
  const round_off = roundMoney(payable - merchandise);
  return { merchandise, payable, round_off };
}

/**
 * @param {number} merchandiseTotal
 * @param {string} [paymentMethod] CASH | UPI | CARD
 */
export function payableForTender(merchandiseTotal, paymentMethod) {
  const method = String(paymentMethod || '').toUpperCase();
  if (method === 'CASH') {
    return cashRoundOff(merchandiseTotal);
  }
  const merchandise = roundMoney(merchandiseTotal);
  return { merchandise, payable: merchandise, round_off: 0 };
}

/** Last 10 digits, or empty string. */
export function phoneDigits(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.length >= 10 ? digits.slice(-10) : digits;
}

/**
 * Normalize for POS charge/amend body.
 * @returns {string|undefined} 10-digit local, '' to clear (amend), or undefined to omit
 */
export function normalizeCustomerPhoneForApi(value, { allowEmpty = false } = {}) {
  if (value === '' && allowEmpty) return '';
  if (value == null || value === '') return undefined;
  const local = phoneDigits(value);
  if (local.length === 10) return local;
  return undefined;
}

/**
 * Calculate the effective discount percentage for a sale / receipt.
 * Prefers explicit discount_percent if provided and > 0, otherwise
 * computes (discount_amount / subtotal) * 100.
 *
 * @param {object} saleOrReceipt
 * @returns {number|null}
 */
export function getReceiptDiscountPercent(saleOrReceipt) {
  if (!saleOrReceipt || typeof saleOrReceipt !== 'object') return null;
  const explicit = Number(
    saleOrReceipt.discount_percent
    ?? saleOrReceipt.discountPercent
    ?? saleOrReceipt.discount_percentage,
  );
  if (Number.isFinite(explicit) && explicit > 0) {
    return explicit;
  }
  const discount = Number(saleOrReceipt.discount_amount ?? saleOrReceipt.discountAmount) || 0;
  if (discount <= 0) return null;

  let subtotal = Number(saleOrReceipt.subtotal) || 0;
  if (subtotal <= 0 && Array.isArray(saleOrReceipt.line_items) && saleOrReceipt.line_items.length > 0) {
    subtotal = saleOrReceipt.line_items.reduce((sum, it) => sum + (Number(it.line_total) || 0), 0);
  }
  if (subtotal <= 0) {
    const total = Number(saleOrReceipt.total) || 0;
    if (total > 0) subtotal = total + discount;
  }

  if (discount > 0 && subtotal > 0) {
    return Math.min(100, (discount / subtotal) * 100);
  }
  return null;
}

/**
 * Format a discount percent number as e.g. "10%" or "12.5%".
 * Up to 2 decimal places, omitting trailing zeros.
 *
 * @param {number|null|undefined} pct
 * @returns {string}
 */
export function formatDiscountPercent(pct) {
  if (pct == null || !Number.isFinite(Number(pct)) || Number(pct) <= 0) return '';
  const num = Number(pct);
  const rounded = Math.round(num * 100) / 100;
  return `${rounded}%`;
}

/**
 * Builds the discount line label for receipts / bill summary.
 * e.g. "Discount (SAVE10 · 10%)", "Discount (10%)", "Discount (SAVE10)", or "Discount".
 * In ASCII mode (for thermal ESC/POS), uses separator like ", " instead of " · ".
 *
 * @param {object} receipt
 * @param {object} [options]
 * @param {boolean} [options.ascii]
 * @returns {string}
 */
export function formatDiscountLabel(receipt, { ascii = false } = {}) {
  const code = receipt?.coupon_code || receipt?.couponCode || null;
  const pct = getReceiptDiscountPercent(receipt);
  const pctStr = pct ? formatDiscountPercent(pct) : '';
  const separator = ascii ? ', ' : ' · ';
  const details = [code, pctStr].filter(Boolean).join(separator);
  return details ? `Discount (${details})` : 'Discount';
}

/**
 * Resolve the MRP for an item, from item itself or from the catalog products array.
 *
 * @param {object} item
 * @param {Array} [products]
 * @returns {number|null}
 */
export function getItemMrp(item, products = []) {
  if (item?.mrp != null && Number(item.mrp) > 0) {
    return Number(item.mrp);
  }
  const id = item?.item_id || item?.id;
  const sku = item?.sku;
  const product = products.find((p) => (id && p.id === id) || (sku && p.sku === sku));
  if (product) {
    if (item?.lot_id && Array.isArray(product.lots)) {
      const lot = product.lots.find((l) => l.lot_id === item.lot_id);
      if (lot?.mrp != null && Number(lot.mrp) > 0) return Number(lot.mrp);
    }
    if (product.mrp != null && Number(product.mrp) > 0) {
      return Number(product.mrp);
    }
  }
  return null;
}

/**
 * Calculate per-item discount savings comparing MRP to unit price.
 *
 * @param {object} item
 * @param {number|null} [explicitMrp]
 * @returns {{ unitSavings: number, totalSavings: number, percent: number, mrp: number|null }}
 */
export function getItemSavings(item, explicitMrp) {
  const price = Number(item?.unit_price ?? item?.price) || 0;
  const mrp = explicitMrp != null ? Number(explicitMrp) : (Number(item?.mrp) || null);
  if (!mrp || mrp <= price) {
    return { unitSavings: 0, totalSavings: 0, percent: 0, mrp };
  }
  const qty = Number(item?.qty) || 1;
  const unitSavings = roundMoney(mrp - price);
  const totalSavings = roundMoney(unitSavings * qty);
  const percent = Math.min(100, Math.round(((mrp - price) / mrp) * 1000) / 10);
  return { unitSavings, totalSavings, percent, mrp };
}

/**
 * Calculate overall bill savings (item-level MRP savings + coupon/bill discounts).
 *
 * @param {object} receipt
 * @param {Array} [products]
 * @returns {{ itemSavingsTotal: number, couponDiscount: number, totalSavings: number, totalSavingsPct: number, totalMrp: number }}
 */
export function calculateBillSavings(receipt, products = []) {
  const lineItems = receipt?.line_items || [];
  let itemSavingsTotal = 0;
  let totalMrp = 0;

  lineItems.forEach((it) => {
    const mrp = getItemMrp(it, products);
    const savings = getItemSavings(it, mrp);
    itemSavingsTotal += savings.totalSavings;
    const price = Number(it.unit_price ?? it.price) || 0;
    const qty = Number(it.qty) || 1;
    totalMrp += (savings.mrp || price) * qty;
  });

  const couponDiscount = Number(
    receipt?.discount_amount
    ?? receipt?.discount
    ?? receipt?.discountAmount
  ) || 0;

  const totalSavings = roundMoney(itemSavingsTotal + couponDiscount);
  const baseline = totalMrp > 0 ? totalMrp : ((Number(receipt?.subtotal) || 0) + couponDiscount);
  const totalSavingsPct = baseline > 0
    ? Math.min(100, Math.round((totalSavings / baseline) * 1000) / 10)
    : 0;

  return {
    itemSavingsTotal: roundMoney(itemSavingsTotal),
    couponDiscount,
    totalSavings,
    totalSavingsPct,
    totalMrp: roundMoney(totalMrp),
  };
}

/**
 * Format per-item savings text for display in receipt item lines.
 *
 * @param {object} savings  Result from getItemSavings(it, mrp)
 * @param {object} it       Line item
 * @param {Function} formatCurrency
 * @param {object} [options]
 * @param {boolean} [options.ascii]
 * @returns {string}
 */
export function formatItemSavingsText(savings, it, formatCurrency, { ascii = false } = {}) {
  if (!savings || savings.unitSavings <= 0) return '';
  const byWeight = String(it?.soldBy || it?.sold_by || '').toUpperCase() === 'WEIGHT';
  const unitSuffix = byWeight ? `/${it?.unit || 'kg'}` : '';
  const mrpFormatted = formatCurrency ? formatCurrency(savings.mrp) : `${savings.mrp}`;
  const mrpLabel = `MRP ${mrpFormatted}${unitSuffix}`;
  const pctStr = savings.percent > 0 ? formatDiscountPercent(savings.percent) : '';
  const qty = Number(it?.qty) || 1;
  const sep = ascii ? ', ' : ' · ';

  if (byWeight) {
    const saveFormatted = formatCurrency ? formatCurrency(savings.totalSavings) : `${savings.totalSavings}`;
    const pctPart = pctStr ? ` (${pctStr})` : '';
    return `${mrpLabel}${sep}Save ${saveFormatted}${pctPart}`;
  }

  if (qty > 1) {
    const totalSaveFormatted = formatCurrency ? formatCurrency(savings.totalSavings) : `${savings.totalSavings}`;
    const unitSaveFormatted = formatCurrency ? formatCurrency(savings.unitSavings) : `${savings.unitSavings}`;
    const detail = [`${unitSaveFormatted}/pc`, pctStr].filter(Boolean).join(sep);
    return `${mrpLabel}${sep}Save ${totalSaveFormatted} (${detail})`;
  }

  const saveFormatted = formatCurrency ? formatCurrency(savings.unitSavings) : `${savings.unitSavings}`;
  const pctPart = pctStr ? ` (${pctStr})` : '';
  return `${mrpLabel}${sep}Save ${saveFormatted}${pctPart}`;
}


